import React, { useEffect, useMemo, useRef, useState } from "react";
import { customFetch } from "../../utils/Auth";
import ChartHistoryTable from "../Support/ChartHistoryTable";
import { formatChartHistoryFieldLabel } from "../Support/chartHistoryUtils";
import { environment } from "../../config/environment";
import CodingPanel from "./CodingPanel";

const CASE_ROLE_OPTIONS = [
  { id: "documentation", label: "Documentation", roles: ["documentation"] },
  { id: "coding", label: "Coding", roles: ["coding"] },
  { id: "both", label: "Both", roles: ["documentation", "coding"] },
];
const formatCaseRoles = (roles) => {
  const list = Array.isArray(roles) ? roles : [];
  if (list.includes("documentation") && list.includes("coding")) return "Both";
  if (list.includes("coding")) return "Coding";
  if (list.includes("documentation")) return "Documentation";
  return "-";
};
const extractCoding = (data) =>
  data?.coding && typeof data.coding === "object" ? data.coding : null;

const COPIED_POPOVER_CLOSE_DELAY_MS = 140;
const COPIED_POPOVER_WIDTH_PX = 760;
const COPIED_POPOVER_MIN_HEIGHT_PX = 260;
const COPIED_POPOVER_MAX_HEIGHT_PX = 420;
const COPIED_POPOVER_HEIGHT_ESTIMATE_PX = COPIED_POPOVER_MAX_HEIGHT_PX;
const COPIED_POPOVER_CURSOR_GAP_PX = 14;
const COPIED_POPOVER_CURSOR_VERTICAL_GAP_PX = 14;
const CANDIDATE_PREVIEW_WIDTH_PX = 1120;
const CANDIDATE_PREVIEW_MIN_HEIGHT_PX = 420;
const CANDIDATE_PREVIEW_MAX_HEIGHT_PX = 720;
const CANDIDATE_PREVIEW_HEIGHT_ESTIMATE_PX = CANDIDATE_PREVIEW_MAX_HEIGHT_PX;
const CANDIDATE_PREVIEW_CURSOR_GAP_PX = 14;
const CANDIDATE_PREVIEW_CURSOR_VERTICAL_GAP_PX = 14;
const normalizeJobNameList = (value) =>
  (Array.isArray(value) ? value : value ? [value] : [])
    .map((entry) => String(entry || "").trim())
    .filter(Boolean);

export default function QAEvalCaseQuery() {
  const [appointmentSearch, setAppointmentSearch] = useState("");
  const [candidateRows, setCandidateRows] = useState([]);
  const [configuredRows, setConfiguredRows] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState("");
  const [appointmentId, setAppointmentId] = useState("");
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [selectedRoleOption, setSelectedRoleOption] = useState("documentation");
  const [selectedHasCodingData, setSelectedHasCodingData] = useState(false);
  const [activeOnDate, setActiveOnDate] = useState("");
  const [selectedDaysRemaining, setSelectedDaysRemaining] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [selectingPatientId, setSelectingPatientId] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [configuredCaseSort, setConfiguredCaseSort] = useState({
    field: "active_on_date",
    direction: "desc",
  });
  const [hoveredCandidatePreview, setHoveredCandidatePreview] = useState(null);
  const [isCandidatePreviewLoading, setIsCandidatePreviewLoading] =
    useState(false);
  const [hoveredCopiedCase, setHoveredCopiedCase] = useState(null);
  const [hoveredLiveTranscriptCase, setHoveredLiveTranscriptCase] = useState(null);
  const [candidatePreviewPopoverPosition, setCandidatePreviewPopoverPosition] =
    useState({
      top: 100,
      left: 100,
    });
  const [copiedPopoverPosition, setCopiedPopoverPosition] = useState({
    top: 100,
    left: 100,
  });
  const [liveTranscriptPopoverPosition, setLiveTranscriptPopoverPosition] =
    useState({
      top: 100,
      left: 100,
    });
  const candidatePreviewPopoverCloseTimeoutRef = useRef(null);
  const candidatePreviewCacheRef = useRef({});
  const activeCandidatePreviewKeyRef = useRef("");
  const candidatePreviewChapiScrollRef = useRef(null);
  const copiedPopoverCloseTimeoutRef = useRef(null);
  const liveTranscriptPopoverCloseTimeoutRef = useRef(null);

  const todayString = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, []);

  const clearMessages = () => {
    setErrorMessage("");
    setSuccessMessage("");
  };
  const clearSelectedCase = () => {
    setSelectedPatientId("");
    setAppointmentId("");
    setSelectedJobs([]);
    setSelectedRoleOption("documentation");
    setSelectedHasCodingData(false);
    setActiveOnDate("");
    setSelectedDaysRemaining(null);
    clearMessages();
  };
  const getAppointmentPlaceholder = (patientId) => {
    return "No Appointment ID";
  };

  const normalizePatientId = (value) => String(value || "").trim();
  const configuredPatientIdSet = useMemo(
    () =>
      new Set(
        configuredRows
          .map((row) => normalizePatientId(row?.patient_id))
          .filter(Boolean),
      ),
    [configuredRows],
  );
  const selectableCandidateRows = useMemo(
    () =>
      candidateRows.filter(
        (candidate) =>
          !configuredPatientIdSet.has(normalizePatientId(candidate?.patient_id)),
      ),
    [candidateRows, configuredPatientIdSet],
  );

  const getCandidatePreviewKey = (candidate) => {
    const patientId = normalizePatientId(candidate?.patient_id);
    return `patient::${patientId}`;
  };

  const clearCandidatePreviewPopoverCloseTimeout = () => {
    if (candidatePreviewPopoverCloseTimeoutRef.current) {
      clearTimeout(candidatePreviewPopoverCloseTimeoutRef.current);
      candidatePreviewPopoverCloseTimeoutRef.current = null;
    }
  };

  const closeCandidatePreviewPopover = () => {
    activeCandidatePreviewKeyRef.current = "";
    setHoveredCandidatePreview(null);
    setIsCandidatePreviewLoading(false);
  };

  const scheduleCandidatePreviewPopoverClose = () => {
    clearCandidatePreviewPopoverCloseTimeout();
    candidatePreviewPopoverCloseTimeoutRef.current = setTimeout(() => {
      closeCandidatePreviewPopover();
    }, COPIED_POPOVER_CLOSE_DELAY_MS);
  };

  const clearCopiedPopoverCloseTimeout = () => {
    if (copiedPopoverCloseTimeoutRef.current) {
      clearTimeout(copiedPopoverCloseTimeoutRef.current);
      copiedPopoverCloseTimeoutRef.current = null;
    }
  };

  const clearLiveTranscriptPopoverCloseTimeout = () => {
    if (liveTranscriptPopoverCloseTimeoutRef.current) {
      clearTimeout(liveTranscriptPopoverCloseTimeoutRef.current);
      liveTranscriptPopoverCloseTimeoutRef.current = null;
    }
  };

  const scheduleCopiedPopoverClose = () => {
    clearCopiedPopoverCloseTimeout();
    copiedPopoverCloseTimeoutRef.current = setTimeout(() => {
      setHoveredCopiedCase(null);
    }, COPIED_POPOVER_CLOSE_DELAY_MS);
  };

  const scheduleLiveTranscriptPopoverClose = () => {
    clearLiveTranscriptPopoverCloseTimeout();
    liveTranscriptPopoverCloseTimeoutRef.current = setTimeout(() => {
      setHoveredLiveTranscriptCase(null);
    }, COPIED_POPOVER_CLOSE_DELAY_MS);
  };

  const openCopiedPopover = (
    row,
    { triggerElement, cursorX, cursorY } = {},
  ) => {
    clearCopiedPopoverCloseTimeout();
    closeCandidatePreviewPopover();
    setHoveredLiveTranscriptCase(null);
    if (!row) {
      return;
    }

    const rect = triggerElement?.getBoundingClientRect?.();
    const viewportWidth = window.innerWidth || COPIED_POPOVER_WIDTH_PX;
    const viewportHeight = window.innerHeight || COPIED_POPOVER_HEIGHT_ESTIMATE_PX;
    const edgePadding = 12;
    const panelWidth = Math.min(
      COPIED_POPOVER_WIDTH_PX,
      viewportWidth - edgePadding * 2,
    );
    const maxLeft = Math.max(edgePadding, viewportWidth - panelWidth - edgePadding);
    const hasCursorX = Number.isFinite(cursorX);
    const hasCursorY = Number.isFinite(cursorY);
    const anchorX = hasCursorX
      ? cursorX
      : rect
        ? rect.right
        : edgePadding + panelWidth;
    const anchorY = hasCursorY
      ? cursorY
      : rect
        ? rect.top + rect.height / 2
        : edgePadding;

    let nextLeft = anchorX - panelWidth - COPIED_POPOVER_CURSOR_GAP_PX;
    if (nextLeft < edgePadding) {
      nextLeft = anchorX + COPIED_POPOVER_CURSOR_GAP_PX;
    }
    nextLeft = Math.min(Math.max(edgePadding, nextLeft), maxLeft);

    // Default placement is up-left so the cursor sits near bottom-right outside edge.
    let nextTop =
      anchorY -
      COPIED_POPOVER_HEIGHT_ESTIMATE_PX -
      COPIED_POPOVER_CURSOR_VERTICAL_GAP_PX;
    if (nextTop < edgePadding) {
      // Fallback below cursor when there is no room above.
      nextTop = anchorY + COPIED_POPOVER_CURSOR_VERTICAL_GAP_PX;
    }
    const maxTop = Math.max(
      edgePadding,
      viewportHeight - COPIED_POPOVER_HEIGHT_ESTIMATE_PX - edgePadding,
    );
    nextTop = Math.min(Math.max(edgePadding, nextTop), maxTop);
    nextTop = Math.max(edgePadding, nextTop);

    setCopiedPopoverPosition({ top: nextTop, left: nextLeft });
    setHoveredCopiedCase(row);
  };

  const openLiveTranscriptPopover = (
    row,
    { triggerElement, cursorX, cursorY } = {},
  ) => {
    clearLiveTranscriptPopoverCloseTimeout();
    closeCandidatePreviewPopover();
    setHoveredCopiedCase(null);
    if (!row) {
      return;
    }

    const rect = triggerElement?.getBoundingClientRect?.();
    const viewportWidth = window.innerWidth || COPIED_POPOVER_WIDTH_PX;
    const viewportHeight = window.innerHeight || COPIED_POPOVER_HEIGHT_ESTIMATE_PX;
    const edgePadding = 12;
    const panelWidth = Math.min(
      COPIED_POPOVER_WIDTH_PX,
      viewportWidth - edgePadding * 2,
    );
    const maxLeft = Math.max(edgePadding, viewportWidth - panelWidth - edgePadding);
    const hasCursorX = Number.isFinite(cursorX);
    const hasCursorY = Number.isFinite(cursorY);
    const anchorX = hasCursorX
      ? cursorX
      : rect
        ? rect.right
        : edgePadding + panelWidth;
    const anchorY = hasCursorY
      ? cursorY
      : rect
        ? rect.top + rect.height / 2
        : edgePadding;

    let nextLeft = anchorX - panelWidth - COPIED_POPOVER_CURSOR_GAP_PX;
    if (nextLeft < edgePadding) {
      nextLeft = anchorX + COPIED_POPOVER_CURSOR_GAP_PX;
    }
    nextLeft = Math.min(Math.max(edgePadding, nextLeft), maxLeft);

    // Default placement is up-left so the cursor sits near bottom-right outside edge.
    let nextTop =
      anchorY -
      COPIED_POPOVER_HEIGHT_ESTIMATE_PX -
      COPIED_POPOVER_CURSOR_VERTICAL_GAP_PX;
    if (nextTop < edgePadding) {
      // Fallback below cursor when there is no room above.
      nextTop = anchorY + COPIED_POPOVER_CURSOR_VERTICAL_GAP_PX;
    }
    const maxTop = Math.max(
      edgePadding,
      viewportHeight - COPIED_POPOVER_HEIGHT_ESTIMATE_PX - edgePadding,
    );
    nextTop = Math.min(Math.max(edgePadding, nextTop), maxTop);
    nextTop = Math.max(edgePadding, nextTop);

    setLiveTranscriptPopoverPosition({ top: nextTop, left: nextLeft });
    setHoveredLiveTranscriptCase(row);
  };

  const openCandidatePreviewPopover = async (
    candidate,
    { triggerElement, cursorX, cursorY } = {},
  ) => {
    clearCandidatePreviewPopoverCloseTimeout();
    setHoveredCopiedCase(null);
    setHoveredLiveTranscriptCase(null);
    if (!candidate) {
      return;
    }

    const rect = triggerElement?.getBoundingClientRect?.();
    const viewportWidth = window.innerWidth || CANDIDATE_PREVIEW_WIDTH_PX;
    const viewportHeight =
      window.innerHeight || CANDIDATE_PREVIEW_HEIGHT_ESTIMATE_PX;
    const edgePadding = 12;
    const panelWidth = Math.min(
      CANDIDATE_PREVIEW_WIDTH_PX,
      viewportWidth - edgePadding * 2,
    );
    const maxLeft = Math.max(edgePadding, viewportWidth - panelWidth - edgePadding);
    const hasCursorX = Number.isFinite(cursorX);
    const hasCursorY = Number.isFinite(cursorY);
    const anchorX = hasCursorX
      ? cursorX
      : rect
        ? rect.right
        : edgePadding;
    const anchorY = hasCursorY
      ? cursorY
      : rect
        ? rect.top + rect.height / 2
        : edgePadding;

    // Preview opens to the right by default.
    let nextLeft = anchorX + CANDIDATE_PREVIEW_CURSOR_GAP_PX;
    if (nextLeft + panelWidth > viewportWidth - edgePadding) {
      nextLeft = anchorX - panelWidth - CANDIDATE_PREVIEW_CURSOR_GAP_PX;
    }
    nextLeft = Math.min(Math.max(edgePadding, nextLeft), maxLeft);

    // Bias slightly upward so cursor stays near lower-left outside the panel.
    let nextTop =
      anchorY -
      CANDIDATE_PREVIEW_HEIGHT_ESTIMATE_PX +
      CANDIDATE_PREVIEW_CURSOR_VERTICAL_GAP_PX;
    if (nextTop < edgePadding) {
      nextTop = anchorY + CANDIDATE_PREVIEW_CURSOR_VERTICAL_GAP_PX;
    }
    const maxTop = Math.max(
      edgePadding,
      viewportHeight - CANDIDATE_PREVIEW_HEIGHT_ESTIMATE_PX - edgePadding,
    );
    nextTop = Math.min(Math.max(edgePadding, nextTop), maxTop);
    setCandidatePreviewPopoverPosition({ top: nextTop, left: nextLeft });

    const previewKey = getCandidatePreviewKey(candidate);
    activeCandidatePreviewKeyRef.current = previewKey;
    const patientId = normalizePatientId(candidate?.patient_id);
    const appointmentId = String(candidate?.appointment_id || "").trim();
    const transcribeJobName = String(
      candidate?.latest_transcribe_job_name || "",
    ).trim();
    const summarizeJobName = String(
      candidate?.latest_summarize_job_name || "",
    ).trim();
    const cachedPreview = candidatePreviewCacheRef.current[previewKey];
    const getInitialEntryIndex = (entries = []) => {
      if (!Array.isArray(entries) || entries.length === 0) {
        return 0;
      }
      const exactMatchIndex = entries.findIndex(
        (entry) =>
          String(entry?.transcribe_job_name || "").trim() === transcribeJobName &&
          String(entry?.summarize_job_name || "").trim() === summarizeJobName,
      );
      if (exactMatchIndex >= 0) {
        return exactMatchIndex;
      }
      const summarizeMatchIndex = entries.findIndex(
        (entry) =>
          String(entry?.summarize_job_name || "").trim() === summarizeJobName,
      );
      if (summarizeMatchIndex >= 0) {
        return summarizeMatchIndex;
      }
      const transcribeMatchIndex = entries.findIndex(
        (entry) =>
          String(entry?.transcribe_job_name || "").trim() === transcribeJobName,
      );
      if (transcribeMatchIndex >= 0) {
        return transcribeMatchIndex;
      }
      return 0;
    };

    setHoveredCandidatePreview({
      key: previewKey,
      patientId,
      appointmentId,
      entries: Array.isArray(cachedPreview?.entries) ? cachedPreview.entries : [],
      chartHistory: cachedPreview?.chartHistory || null,
      coding: cachedPreview?.coding || null,
      activeEntryIndex: getInitialEntryIndex(cachedPreview?.entries || []),
      sourceCandidate: candidate,
    });

    if (cachedPreview) {
      setIsCandidatePreviewLoading(false);
      return;
    }
    if (!patientId) {
      setIsCandidatePreviewLoading(false);
      return;
    }

    setIsCandidatePreviewLoading(true);
    try {
      const searchParams = new URLSearchParams();
      searchParams.set("patient_id", patientId);
      if (transcribeJobName) {
        searchParams.set("transcribe_job_name", transcribeJobName);
      }
      if (summarizeJobName) {
        searchParams.set("summarize_job_name", summarizeJobName);
      }

      const response = await customFetch(
        `${environment.apiUrl}/patients/qa-eval/candidate-preview?${searchParams.toString()}`,
        { method: "GET" },
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || "Failed to load candidate preview");
      }

      const previewData = {
        entries: Array.isArray(data?.entries) ? data.entries : [],
        chartHistory:
          data?.prechart_history &&
          typeof data.prechart_history === "object" &&
          !Array.isArray(data.prechart_history)
            ? data.prechart_history
            : null,
        coding: extractCoding(data),
      };
      candidatePreviewCacheRef.current[previewKey] = previewData;

      if (activeCandidatePreviewKeyRef.current === previewKey) {
        setHoveredCandidatePreview((previous) =>
          previous?.key === previewKey
            ? {
                ...previous,
                entries: previewData.entries,
                chartHistory: previewData.chartHistory,
                coding: previewData.coding,
                activeEntryIndex: getInitialEntryIndex(previewData.entries),
              }
            : previous,
        );
      }
    } catch (error) {
      console.error("Failed to load QA candidate preview:", error);
    } finally {
      if (activeCandidatePreviewKeyRef.current === previewKey) {
        setIsCandidatePreviewLoading(false);
      }
    }
  };

  const normalizeCopiedValues = (values) =>
    (Array.isArray(values) ? values : [])
      .map((value) => String(value || "").trim().toLowerCase())
      .filter(
        (value) =>
          value === "transcript" ||
          value === "note" ||
          value === "live_transcript",
      );

  const getLiveTranscriptEntriesForCase = (liveTranscriptRaw) => {
    if (
      !liveTranscriptRaw ||
      typeof liveTranscriptRaw !== "object" ||
      Array.isArray(liveTranscriptRaw)
    ) {
      return [];
    }

    return Object.entries(liveTranscriptRaw)
      .map(([username, textValue]) => {
        const normalizedUsername = String(username || "").trim();
        const normalizedText = String(textValue || "").trim();
        if (!normalizedUsername || !normalizedText) {
          return null;
        }
        return {
          username: normalizedUsername,
          text: normalizedText,
        };
      })
      .filter(Boolean)
      .sort((left, right) => left.username.localeCompare(right.username));
  };

  const getCopiedUsersForCase = (copiedRaw) => {
    if (!copiedRaw || typeof copiedRaw !== "object" || Array.isArray(copiedRaw)) {
      return [];
    }

    return Object.entries(copiedRaw)
      .map(([username, entry]) => {
        const normalizedUsername = String(username || "").trim();
        if (!normalizedUsername) {
          return null;
        }

        const itemsFromEntry = Array.isArray(entry)
          ? entry
          : Array.isArray(entry?.items)
            ? entry.items
            : [];
        const items = Array.from(new Set(normalizeCopiedValues(itemsFromEntry)));
        const noteCountFromItems = items.filter(
          (item) => item === "note",
        ).length;
        const transcriptCountFromItems = items.filter(
          (item) => item === "transcript",
        ).length;
        const liveTranscriptCountFromItems = items.filter(
          (item) => item === "live_transcript",
        ).length;

        const noteCountFromEntry = Number(entry?.counts?.note);
        const transcriptCountFromEntry = Number(entry?.counts?.transcript);
        const liveTranscriptCountFromEntry = Number(
          entry?.counts?.live_transcript,
        );
        const totalFromEntry = Number(entry?.counts?.total);

        const noteCount = Number.isFinite(noteCountFromEntry)
          ? Math.max(noteCountFromItems, noteCountFromEntry)
          : noteCountFromItems;
        const transcriptCount = Number.isFinite(transcriptCountFromEntry)
          ? Math.max(transcriptCountFromItems, transcriptCountFromEntry)
          : transcriptCountFromItems;
        const liveTranscriptCount = Number.isFinite(liveTranscriptCountFromEntry)
          ? Math.max(liveTranscriptCountFromItems, liveTranscriptCountFromEntry)
          : liveTranscriptCountFromItems;
        const totalCount = Number.isFinite(totalFromEntry)
          ? Math.max(noteCount + transcriptCount + liveTranscriptCount, totalFromEntry)
          : noteCount + transcriptCount + liveTranscriptCount;

        if (totalCount <= 0) {
          return null;
        }

        return {
          username: normalizedUsername,
          items,
          noteCount,
          transcriptCount,
          liveTranscriptCount,
          totalCount,
        };
      })
      .filter(Boolean)
      .sort((left, right) => {
        if (right.totalCount !== left.totalCount) {
          return right.totalCount - left.totalCount;
        }
        return left.username.localeCompare(right.username);
      });
  };

  const loadConfiguredCases = async () => {
    const response = await customFetch(
      `${environment.apiUrl}/patients/qa-eval/cases`,
      { method: "GET" },
    );
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.error || "Failed to load configured QA cases");
    }

    setConfiguredRows(Array.isArray(data?.cases) ? data.cases : []);
  };

  const runSearch = async () => {
    const normalizedSearch = String(appointmentSearch || "").trim();
    if (!normalizedSearch) {
      setErrorMessage("Enter an appointment ID or patient ID to search.");
      return;
    }

    clearSelectedCase();
    setIsSearching(true);
    try {
      const response = await customFetch(
        `${environment.apiUrl}/patients/qa-eval/search?search_term=${encodeURIComponent(normalizedSearch)}`,
        { method: "GET" },
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || "Failed to search QA candidates");
      }

      const nextCandidates = Array.isArray(data?.candidates) ? data.candidates : [];
      setCandidateRows(nextCandidates);
      if (nextCandidates.length === 0) {
        setErrorMessage("No matching patient candidates were found.");
      } else {
        const unconfiguredCandidates = nextCandidates.filter(
          (candidate) =>
            !configuredPatientIdSet.has(normalizePatientId(candidate?.patient_id)),
        );
        if (unconfiguredCandidates.length === 0) {
          setErrorMessage("All matching candidates are already configured.");
        }
      }
    } catch (error) {
      console.error("Failed to search QA eval candidates:", error);
      setCandidateRows([]);
      setErrorMessage(error.message || "Unable to search by appointment or patient ID.");
    } finally {
      setIsSearching(false);
    }
  };

  const chooseCandidate = (candidate, options = {}) => {
    const nextPatientId = String(candidate?.patient_id || "").trim();
    if (configuredPatientIdSet.has(nextPatientId)) {
      setErrorMessage("This case is already configured.");
      return;
    }
    const nextAppointmentId = String(candidate?.appointment_id || "").trim();
    const appointmentIdForSelection =
      nextAppointmentId || getAppointmentPlaceholder(nextPatientId);
    const overrideTranscribeJobName = String(
      options?.transcribeJobName || "",
    ).trim();
    const overrideSummarizeJobName = String(
      options?.summarizeJobName || "",
    ).trim();
    const nextJob = {
      transcribeJobName:
        overrideTranscribeJobName ||
        String(candidate?.latest_transcribe_job_name || "").trim(),
      summarizeJobName:
        overrideSummarizeJobName ||
        String(candidate?.latest_summarize_job_name || "").trim(),
    };
    const providedJobs = (Array.isArray(options?.jobs) ? options.jobs : [])
      .map((job) => ({
        transcribeJobName: String(job?.transcribeJobName || "").trim(),
        summarizeJobName: String(job?.summarizeJobName || "").trim(),
      }))
      .filter((job) => job.transcribeJobName && job.summarizeJobName);
    const nextJobs = providedJobs.length > 0 ? providedJobs : [nextJob];
    setSelectedPatientId(nextPatientId);
    setAppointmentId(appointmentIdForSelection);
    setSelectedJobs((previousJobs) => {
      if (!options?.append) {
        return nextJobs;
      }
      const appendedJobs = [...previousJobs];
      nextJobs.forEach((jobToAppend) => {
        const alreadySelected = appendedJobs.some(
          (job) =>
            job.transcribeJobName === jobToAppend.transcribeJobName &&
            job.summarizeJobName === jobToAppend.summarizeJobName,
        );
        if (!alreadySelected) {
          appendedJobs.push(jobToAppend);
        }
      });
      return appendedJobs;
    });
    const hasCodingData = Boolean(candidate?.has_coding_data);
    setSelectedHasCodingData(hasCodingData);
    if (!hasCodingData) {
      setSelectedRoleOption("documentation");
    }
    setActiveOnDate(String(candidate?.active_on_date || todayString).slice(0, 10));
    setSelectedDaysRemaining(
      Number.isFinite(Number(candidate?.days_remaining))
        ? Number(candidate.days_remaining)
        : null,
    );
    clearMessages();
  };

  const selectAllCandidateJobs = async (candidate) => {
    const patientId = normalizePatientId(candidate?.patient_id);
    if (!patientId) {
      setErrorMessage("This candidate does not have a patient ID.");
      return;
    }

    setSelectingPatientId(patientId);
    clearMessages();
    try {
      const previewKey = getCandidatePreviewKey(candidate);
      let entries = candidatePreviewCacheRef.current[previewKey]?.entries;

      if (!Array.isArray(entries)) {
        const response = await customFetch(
          `${environment.apiUrl}/patients/qa-eval/candidate-preview?patient_id=${encodeURIComponent(patientId)}`,
          { method: "GET" },
        );
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.error || "Failed to load candidate jobs");
        }
        entries = Array.isArray(data?.entries) ? data.entries : [];
        const chartHistory =
          data?.prechart_history &&
          typeof data.prechart_history === "object" &&
          !Array.isArray(data.prechart_history)
            ? data.prechart_history
            : null;
        candidatePreviewCacheRef.current[previewKey] = {
          entries,
          chartHistory,
          coding: extractCoding(data),
        };
      }

      const pairedJobs = entries
        .map((entry) => ({
          transcribeJobName: String(
            entry?.transcribe_job_name || "",
          ).trim(),
          summarizeJobName: String(entry?.summarize_job_name || "").trim(),
        }))
        .filter((job) => job.transcribeJobName && job.summarizeJobName);

      if (pairedJobs.length === 0) {
        throw new Error(
          "No complete transcribe and summarize job pairs were found.",
        );
      }

      chooseCandidate(candidate, { jobs: pairedJobs });
    } catch (error) {
      console.error("Failed to select all QA eval candidate jobs:", error);
      setErrorMessage(error.message || "Unable to select candidate jobs.");
    } finally {
      setSelectingPatientId("");
    }
  };

  const saveConfiguredCase = async () => {
    if (!selectedPatientId) {
      setErrorMessage("Select a candidate first.");
      return;
    }
    const appointmentIdForSave =
      String(appointmentId || "").trim() || getAppointmentPlaceholder(selectedPatientId);
    if (!String(appointmentId || "").trim()) {
      setAppointmentId(appointmentIdForSave);
    }

    if (
      selectedJobs.length === 0 ||
      selectedJobs.some(
        (job) => !job.transcribeJobName || !job.summarizeJobName,
      )
    ) {
      setErrorMessage(
        "Select at least one audio with both a transcribe and summarize job.",
      );
      return;
    }

    const roleOption =
      CASE_ROLE_OPTIONS.find((option) => option.id === selectedRoleOption) ||
      CASE_ROLE_OPTIONS[0];

    setIsSaving(true);
    clearMessages();
    try {
      const response = await customFetch(
        `${environment.apiUrl}/patients/qa-eval/cases`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            patient_id: selectedPatientId,
            appointment_id: appointmentIdForSave,
            transcribe_job_names: selectedJobs.map(
              (job) => job.transcribeJobName,
            ),
            summarize_job_names: selectedJobs.map(
              (job) => job.summarizeJobName,
            ),
            role: roleOption.roles,
          }),
        },
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || "Failed to save QA eval case");
      }

      setSuccessMessage("QA case saved. It is now available for VA Eval review.");
      await loadConfiguredCases();
    } catch (error) {
      console.error("Failed to save QA eval case:", error);
      setErrorMessage(error.message || "Unable to save QA eval case.");
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      try {
        await loadConfiguredCases();
      } catch (error) {
        console.error("Failed to load configured QA cases:", error);
        setErrorMessage("Unable to load configured QA cases.");
      } finally {
        setIsInitialLoading(false);
      }
    };

    void bootstrap();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(
    () => () => {
      clearCandidatePreviewPopoverCloseTimeout();
      clearCopiedPopoverCloseTimeout();
      clearLiveTranscriptPopoverCloseTimeout();
    },
    [],
  );

  const copiedModalUsers = useMemo(
    () => getCopiedUsersForCase(hoveredCopiedCase?.copied),
    [hoveredCopiedCase],
  );
  const copiedModalTotalActions = copiedModalUsers.reduce(
    (sum, user) => sum + user.totalCount,
    0,
  );
  const liveTranscriptPopoverEntries = useMemo(
    () => getLiveTranscriptEntriesForCase(hoveredLiveTranscriptCase?.live_transcript),
    [hoveredLiveTranscriptCase],
  );
  const hoveredCandidatePreviewEntries = useMemo(
    () =>
      Array.isArray(hoveredCandidatePreview?.entries)
        ? hoveredCandidatePreview.entries
        : [],
    [hoveredCandidatePreview],
  );
  const activeCandidatePreviewEntryIndex = useMemo(() => {
    const rawIndex = Number(hoveredCandidatePreview?.activeEntryIndex || 0);
    if (hoveredCandidatePreviewEntries.length === 0) {
      return 0;
    }
    return Math.min(
      hoveredCandidatePreviewEntries.length - 1,
      Math.max(0, rawIndex),
    );
  }, [hoveredCandidatePreview, hoveredCandidatePreviewEntries]);
  const activeCandidatePreviewEntry =
    hoveredCandidatePreviewEntries[activeCandidatePreviewEntryIndex] || null;
  const setCandidatePreviewEntryIndex = (nextIndex) => {
    setHoveredCandidatePreview((previous) => {
      if (!previous) {
        return previous;
      }
      const entries = Array.isArray(previous.entries) ? previous.entries : [];
      if (entries.length === 0) {
        return {
          ...previous,
          activeEntryIndex: 0,
        };
      }
      const clampedIndex = Math.min(
        entries.length - 1,
        Math.max(0, Number(nextIndex || 0)),
      );
      return {
        ...previous,
        activeEntryIndex: clampedIndex,
      };
    });
  };
  const scrollToCandidatePreviewChapiField = (field) => {
    const scrollContainer = candidatePreviewChapiScrollRef.current;
    if (!scrollContainer) {
      return;
    }
    const fieldSection = Array.from(
      scrollContainer.querySelectorAll("[data-chart-history-field]"),
    ).find(
      (section) => section.getAttribute("data-chart-history-field") === field,
    );
    if (!fieldSection) {
      return;
    }

    const containerRect = scrollContainer.getBoundingClientRect();
    const sectionRect = fieldSection.getBoundingClientRect();
    scrollContainer.scrollTo({
      top:
        scrollContainer.scrollTop +
        sectionRect.top -
        containerRect.top -
        8,
      behavior: "smooth",
    });
  };
  const canSelectActiveCandidatePreviewEntry = Boolean(
    String(activeCandidatePreviewEntry?.transcribe_job_name || "").trim() &&
      String(activeCandidatePreviewEntry?.summarize_job_name || "").trim(),
  );
  const isActiveCandidatePreviewEntrySelected = selectedJobs.some(
    (job) =>
      job.transcribeJobName ===
        String(activeCandidatePreviewEntry?.transcribe_job_name || "").trim() &&
      job.summarizeJobName ===
        String(activeCandidatePreviewEntry?.summarize_job_name || "").trim(),
  );
  const completeCandidatePreviewJobs = hoveredCandidatePreviewEntries
    .map((entry) => ({
      transcribeJobName: String(entry?.transcribe_job_name || "").trim(),
      summarizeJobName: String(entry?.summarize_job_name || "").trim(),
    }))
    .filter((job) => job.transcribeJobName && job.summarizeJobName);
  const areAllCandidatePreviewJobsSelected =
    completeCandidatePreviewJobs.length > 0 &&
    selectedPatientId === hoveredCandidatePreview?.patientId &&
    completeCandidatePreviewJobs.every((candidateJob) =>
      selectedJobs.some(
        (selectedJob) =>
          selectedJob.transcribeJobName === candidateJob.transcribeJobName &&
          selectedJob.summarizeJobName === candidateJob.summarizeJobName,
      ),
    );
  const candidatePreviewChartHistory =
    hoveredCandidatePreview?.chartHistory &&
    typeof hoveredCandidatePreview.chartHistory === "object" &&
    !Array.isArray(hoveredCandidatePreview.chartHistory)
      ? hoveredCandidatePreview.chartHistory
      : null;
  const hasCandidatePreviewChartHistory =
    candidatePreviewChartHistory &&
    Object.keys(candidatePreviewChartHistory).length > 0;
  const sortedConfiguredRows = useMemo(() => {
    const rowsWithMetrics = configuredRows.map((row) => {
      const copiedUsers = getCopiedUsersForCase(row?.copied);
      const copiedActionCount = copiedUsers.reduce(
        (sum, user) => sum + user.totalCount,
        0,
      );
      const liveTranscriptEntries = getLiveTranscriptEntriesForCase(
        row?.live_transcript,
      );

      return {
        ...row,
        __copiedActionCount: copiedActionCount,
        __liveTranscriptEntries: liveTranscriptEntries,
        __liveTranscriptCount: liveTranscriptEntries.length,
      };
    });

    const normalizeString = (value) => String(value || "").toLowerCase();
    const normalizeNumber = (value) => {
      const asNumber = Number(value);
      return Number.isFinite(asNumber) ? asNumber : Number.NEGATIVE_INFINITY;
    };
    const normalizeDate = (value) => {
      const parsed = Date.parse(String(value || ""));
      return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
    };
    const directionMultiplier = configuredCaseSort.direction === "asc" ? 1 : -1;

    const compareByField = (leftRow, rightRow) => {
      switch (configuredCaseSort.field) {
        case "appointment_id":
          return (
            normalizeString(leftRow.appointment_id).localeCompare(
              normalizeString(rightRow.appointment_id),
              undefined,
              { numeric: true, sensitivity: "base" },
            ) * directionMultiplier
          );
        case "patient_id":
          return (
            normalizeString(leftRow.patient_id).localeCompare(
              normalizeString(rightRow.patient_id),
              undefined,
              { numeric: true, sensitivity: "base" },
            ) * directionMultiplier
          );
        case "active_on_date":
          return (
            (normalizeDate(leftRow.active_on_date) -
              normalizeDate(rightRow.active_on_date)) * directionMultiplier
          );
        case "days_remaining":
          return (
            (normalizeNumber(leftRow.days_remaining) -
              normalizeNumber(rightRow.days_remaining)) * directionMultiplier
          );
        case "transcribe_job_name":
          return (
            normalizeString(leftRow.transcribe_job_names).localeCompare(
              normalizeString(rightRow.transcribe_job_names),
              undefined,
              { numeric: true, sensitivity: "base" },
            ) * directionMultiplier
          );
        case "summarize_job_name":
          return (
            normalizeString(leftRow.summarize_job_names).localeCompare(
              normalizeString(rightRow.summarize_job_names),
              undefined,
              { numeric: true, sensitivity: "base" },
            ) * directionMultiplier
          );
        case "role":
          return (
            formatCaseRoles(leftRow.role).localeCompare(
              formatCaseRoles(rightRow.role),
            ) * directionMultiplier
          );
        case "live_transcript_count":
          return (
            (normalizeNumber(leftRow.__liveTranscriptCount) -
              normalizeNumber(rightRow.__liveTranscriptCount)) *
            directionMultiplier
          );
        case "copied_count":
          return (
            (normalizeNumber(leftRow.__copiedActionCount) -
              normalizeNumber(rightRow.__copiedActionCount)) *
            directionMultiplier
          );
        default:
          return 0;
      }
    };

    return [...rowsWithMetrics].sort((leftRow, rightRow) => {
      const primaryCompare = compareByField(leftRow, rightRow);
      if (primaryCompare !== 0) {
        return primaryCompare;
      }
      return normalizeString(leftRow.patient_id).localeCompare(
        normalizeString(rightRow.patient_id),
        undefined,
        { numeric: true, sensitivity: "base" },
      );
    });
  }, [configuredRows, configuredCaseSort]);

  const onConfiguredCaseSort = (field) => {
    setConfiguredCaseSort((previousSort) => {
      if (previousSort.field === field) {
        return {
          field,
          direction: previousSort.direction === "asc" ? "desc" : "asc",
        };
      }

      const defaultDirection =
        field === "active_on_date" ||
        field === "days_remaining" ||
        field === "live_transcript_count" ||
        field === "copied_count"
          ? "desc"
          : "asc";

      return {
        field,
        direction: defaultDirection,
      };
    });
  };

  const getConfiguredSortIndicator = (field) =>
    configuredCaseSort.field === field
      ? configuredCaseSort.direction === "asc"
        ? " \u2191"
        : " \u2193"
      : "";

  if (isInitialLoading) {
    return (
      <div className="bg-white shadow-knowtex-shadow rounded-xl p-6 min-h-[280px] flex flex-col items-center justify-center gap-3">
        <div className="h-10 w-10 rounded-full border-4 border-ktx-purple/20 border-t-ktx-purple animate-spin" />
        <p className="text-sm font-medium text-ktx-navy/80">
          Loading QA workspace...
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white shadow-knowtex-shadow rounded-xl p-4">
        <h2 className="text-[22px] font-semibold text-ktx-navy mb-3">
          QA Case Curation
        </h2>
        <p className="text-sm text-ktx-navy/80 mb-4">
          Find candidate patients by appointment ID or patient ID, assign job names,
          and save curated rows for VA Eval review.
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Search appointment ID or patient ID"
            value={appointmentSearch}
            onChange={(event) => setAppointmentSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                void runSearch();
              }
            }}
            className="h-10 flex-1 rounded-lg border-none bg-gray-100 px-4 text-ktx-navy placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-ktx-purple"
          />
          <button
            type="button"
            onClick={() => {
              void runSearch();
            }}
            disabled={isSearching}
            className="h-10 bg-ktx-purple hover:bg-ktx-navy disabled:bg-gray-300 text-white rounded-lg px-4 font-semibold"
          >
            {isSearching ? "Searching..." : "Search"}
          </button>
        </div>
      </div>

      <div className="bg-white shadow-knowtex-shadow rounded-xl p-4">
        <h3 className="text-[18px] font-semibold text-ktx-navy mb-3">Save QA Case</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            type="text"
            placeholder="patient_id"
            value={selectedPatientId}
            onChange={(event) => setSelectedPatientId(event.target.value)}
            className="h-10 rounded-lg border-none bg-gray-100 px-4 text-ktx-navy placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-ktx-purple"
          />
          <input
            type="text"
            placeholder="appointment_id"
            value={appointmentId}
            onChange={(event) => setAppointmentId(event.target.value)}
            className="h-10 rounded-lg border-none bg-gray-100 px-4 text-ktx-navy placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-ktx-purple"
          />
          <textarea
            placeholder="Select one or more transcribe jobs"
            value={selectedJobs
              .map((job, index) => `${index + 1}. ${job.transcribeJobName}`)
              .join("\n")}
            readOnly
            className="min-h-[80px] resize-none rounded-lg border-none bg-gray-100 px-4 py-2 text-ktx-navy placeholder:text-gray-500 focus:outline-none"
          />
          <textarea
            placeholder="Select one or more summarize jobs"
            value={selectedJobs
              .map((job, index) => `${index + 1}. ${job.summarizeJobName}`)
              .join("\n")}
            readOnly
            className="min-h-[80px] resize-none rounded-lg border-none bg-gray-100 px-4 py-2 text-ktx-navy placeholder:text-gray-500 focus:outline-none"
          />
          <input
            type="text"
            value={activeOnDate ? `Active Date: ${activeOnDate}` : "Active Date: (derived from patient date_of_appt)"}
            readOnly
            className="h-10 rounded-lg border-none bg-gray-100 px-4 text-ktx-navy focus:outline-none"
          />
          <input
            type="text"
            value={
              selectedDaysRemaining !== null
                ? `Days Remaining: ${selectedDaysRemaining}`
                : "Days Remaining: (computed after candidate selection)"
            }
            readOnly
            className="h-10 rounded-lg border-none bg-gray-100 px-4 text-ktx-navy focus:outline-none"
          />
          <div className="md:col-span-2">
            <p className="mb-2 text-sm font-semibold text-ktx-navy">
              Review track
            </p>
            <div
              className="flex flex-wrap gap-2"
              role="radiogroup"
              aria-label="Review track"
            >
              {CASE_ROLE_OPTIONS.map((option) => {
                const needsCoding = option.roles.includes("coding");
                const isDisabled =
                  isSaving || (needsCoding && !selectedHasCodingData);
                const isActive = selectedRoleOption === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    role="radio"
                    aria-checked={isActive}
                    disabled={isDisabled}
                    onClick={() => setSelectedRoleOption(option.id)}
                    className={`rounded-lg border px-4 py-2 text-sm font-semibold transition ${
                      isActive
                        ? "border-ktx-purple bg-ktx-purple text-white"
                        : "border-gray-300 bg-white text-ktx-navy hover:bg-gray-50"
                    } disabled:cursor-not-allowed disabled:opacity-40`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
            {selectedPatientId && !selectedHasCodingData && (
              <p className="mt-2 text-xs text-ktx-navy/70">
                This patient has no E&M or ICD-10 data, so only the
                documentation track is available.
              </p>
            )}
          </div>
          <div className="flex gap-3 md:col-span-2">
            <button
              type="button"
              onClick={() => {
                void saveConfiguredCase();
              }}
              disabled={isSaving}
              className="h-10 flex-1 rounded-lg bg-ktx-purple text-white font-semibold hover:bg-ktx-navy disabled:bg-gray-300"
            >
              {isSaving
                ? "Saving..."
                : `Save QA Case (${selectedJobs.length} audio${
                    selectedJobs.length === 1 ? "" : "s"
                  })`}
            </button>
            <button
              type="button"
              onClick={clearSelectedCase}
              disabled={isSaving || (!selectedPatientId && selectedJobs.length === 0)}
              className="h-10 rounded-lg border border-gray-300 bg-white px-5 font-semibold text-ktx-navy hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Clear
            </button>
          </div>
        </div>
        {errorMessage && <p className="mt-3 text-red-600 font-medium">{errorMessage}</p>}
        {successMessage && (
          <p className="mt-3 text-green-700 font-medium">{successMessage}</p>
        )}
      </div>

      {candidateRows.length > 0 && (
        <div className="bg-white shadow-knowtex-shadow rounded-xl p-4">
          <h3 className="text-[18px] font-semibold text-ktx-navy mb-3">
            Candidate Matches
          </h3>
          <div className="max-h-[360px] xl:max-h-[460px] overflow-auto border border-gray-100 rounded-xl">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-gray-200 text-left text-gray-600">
                  <th className="px-4 py-3 font-semibold">Appointment</th>
                  <th className="px-4 py-3 font-semibold">Patient ID</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Jobs Found</th>
                  <th className="px-4 py-3 font-semibold">Audio Files</th>
                  <th className="px-4 py-3 font-semibold">Coding Data</th>
                  <th className="px-4 py-3 font-semibold">Active Date</th>
                  <th className="px-4 py-3 font-semibold">Days Remaining</th>
                  <th className="px-4 py-3 font-semibold text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {selectableCandidateRows.map((candidate) => {
                  const patientId = String(candidate.patient_id || "");
                  const isSelected = patientId === selectedPatientId;
                  return (
                    <tr
                      key={`${candidate.patient_id}-${candidate.appointment_id}`}
                      className={isSelected ? "bg-barely-purple" : "hover:bg-gray-50"}
                    >
                      <td className="px-4 py-3 text-ktx-navy font-medium">
                        {candidate.appointment_id || "-"}
                      </td>
                      <td className="px-4 py-3 text-ktx-navy">{patientId || "-"}</td>
                      <td className="px-4 py-3 text-ktx-navy">
                        {candidate.status || "-"}
                      </td>
                      <td
                        className="px-4 py-3 text-ktx-navy cursor-help"
                        onMouseEnter={(event) => {
                          void openCandidatePreviewPopover(candidate, {
                            triggerElement: event.currentTarget,
                            cursorX: event.clientX,
                            cursorY: event.clientY,
                          });
                        }}
                        onMouseLeave={scheduleCandidatePreviewPopoverClose}
                      >
                        <div className="flex flex-col">
                          <span>
                            Transcribe: {candidate.has_transcribe_job ? "Yes" : "No"}
                            {candidate.transcribe_job_count > 0
                              ? ` (${candidate.transcribe_job_count})`
                              : ""}
                          </span>
                          <span>
                            Summarize: {candidate.has_summarize_job ? "Yes" : "No"}
                            {candidate.summarize_job_count > 0
                              ? ` (${candidate.summarize_job_count})`
                              : ""}
                          </span>
                        </div>
                      </td>
                      <td
                        className={`px-4 py-3 ${
                          candidate.has_multiple_audio_files
                            ? "text-red-600 font-semibold"
                            : "text-ktx-navy"
                        }`}
                      >
                        {candidate.audio_file_count || 0}
                        {candidate.has_multiple_audio_files ? " (multiple)" : ""}
                      </td>
                      <td className="px-4 py-3 text-ktx-navy">
                        {candidate.has_coding_data ? "Yes" : "No"}
                      </td>
                      <td className="px-4 py-3 text-ktx-navy">
                        {candidate.active_on_date || "-"}
                      </td>
                      <td className="px-4 py-3 text-ktx-navy">
                        {Number.isFinite(Number(candidate.days_remaining))
                          ? candidate.days_remaining
                          : "-"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => {
                            void selectAllCandidateJobs(candidate);
                          }}
                          disabled={selectingPatientId === patientId}
                          className="rounded-md bg-ktx-purple px-3 py-1 text-sm font-semibold text-white hover:bg-ktx-navy disabled:cursor-wait disabled:bg-gray-300"
                        >
                          {selectingPatientId === patientId
                            ? "Selecting..."
                            : "Select"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {selectableCandidateRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-4 py-6 text-center text-gray-500 font-medium"
                    >
                      All matching candidates are already configured.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bg-white shadow-knowtex-shadow rounded-xl p-4">
        <h3 className="text-[18px] font-semibold text-ktx-navy mb-3">
          Configured QA Cases
        </h3>
        <div className="max-h-[360px] xl:max-h-[460px] overflow-auto border border-gray-100 rounded-xl">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b border-gray-200 text-left text-gray-600">
                <th className="px-4 py-3 font-semibold">
                  <button
                    type="button"
                    onClick={() => onConfiguredCaseSort("appointment_id")}
                    className="font-semibold hover:text-ktx-purple"
                  >
                    Appointment{getConfiguredSortIndicator("appointment_id")}
                  </button>
                </th>
                <th className="px-4 py-3 font-semibold">
                  <button
                    type="button"
                    onClick={() => onConfiguredCaseSort("patient_id")}
                    className="font-semibold hover:text-ktx-purple"
                  >
                    Patient ID{getConfiguredSortIndicator("patient_id")}
                  </button>
                </th>
                <th className="px-4 py-3 font-semibold">
                  <button
                    type="button"
                    onClick={() => onConfiguredCaseSort("active_on_date")}
                    className="font-semibold hover:text-ktx-purple"
                  >
                    Active Date{getConfiguredSortIndicator("active_on_date")}
                  </button>
                </th>
                <th className="px-4 py-3 font-semibold">
                  <button
                    type="button"
                    onClick={() => onConfiguredCaseSort("days_remaining")}
                    className="font-semibold hover:text-ktx-purple"
                  >
                    Days Remaining{getConfiguredSortIndicator("days_remaining")}
                  </button>
                </th>
                <th className="px-4 py-3 font-semibold">
                  <button
                    type="button"
                    onClick={() => onConfiguredCaseSort("role")}
                    className="font-semibold hover:text-ktx-purple"
                  >
                    Track{getConfiguredSortIndicator("role")}
                  </button>
                </th>
                <th className="px-4 py-3 font-semibold">
                  <button
                    type="button"
                    onClick={() => onConfiguredCaseSort("transcribe_job_name")}
                    className="font-semibold hover:text-ktx-purple"
                  >
                    Transcribe Job{getConfiguredSortIndicator("transcribe_job_name")}
                  </button>
                </th>
                <th className="px-4 py-3 font-semibold">
                  <button
                    type="button"
                    onClick={() => onConfiguredCaseSort("summarize_job_name")}
                    className="font-semibold hover:text-ktx-purple"
                  >
                    Summarize Job{getConfiguredSortIndicator("summarize_job_name")}
                  </button>
                </th>
                <th className="px-4 py-3 font-semibold text-right">
                  <button
                    type="button"
                    onClick={() => onConfiguredCaseSort("live_transcript_count")}
                    className="font-semibold hover:text-ktx-purple"
                  >
                    Live Transcript
                    {getConfiguredSortIndicator("live_transcript_count")}
                  </button>
                </th>
                <th className="px-4 py-3 font-semibold text-right">
                  <button
                    type="button"
                    onClick={() => onConfiguredCaseSort("copied_count")}
                    className="font-semibold hover:text-ktx-purple"
                  >
                    Copied{getConfiguredSortIndicator("copied_count")}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedConfiguredRows.map((row) => {
                const copiedActionCount = Number(row.__copiedActionCount || 0);
                const liveTranscriptEntries = Array.isArray(
                  row.__liveTranscriptEntries,
                )
                  ? row.__liveTranscriptEntries
                  : [];

                return (
                  <tr
                    key={`${row.patient_id}-${row.created_at}`}
                    className="border-b border-gray-100"
                  >
                    <td className="px-4 py-3 text-ktx-navy font-medium">
                      {row.appointment_id || "-"}
                    </td>
                    <td className="px-4 py-3 text-ktx-navy">{row.patient_id || "-"}</td>
                    <td className="px-4 py-3 text-ktx-navy">{row.active_on_date || "-"}</td>
                    <td className="px-4 py-3 text-ktx-navy">
                      {Number.isFinite(Number(row.days_remaining))
                        ? row.days_remaining
                        : "-"}
                    </td>
                    <td className="px-4 py-3 text-ktx-navy">
                      {formatCaseRoles(row.role)}
                    </td>
                    <td className="px-4 py-3 text-ktx-navy">
                      {normalizeJobNameList(row.transcribe_job_names).join(", ") ||
                        "-"}
                    </td>
                    <td className="px-4 py-3 text-ktx-navy">
                      {normalizeJobNameList(row.summarize_job_names).join(", ") ||
                        "-"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onMouseEnter={(event) => {
                          openLiveTranscriptPopover(row, {
                            triggerElement: event.currentTarget,
                            cursorX: event.clientX,
                            cursorY: event.clientY,
                          });
                        }}
                        onMouseLeave={scheduleLiveTranscriptPopoverClose}
                        onFocus={(event) => {
                          openLiveTranscriptPopover(row, {
                            triggerElement: event.currentTarget,
                          });
                        }}
                        onBlur={scheduleLiveTranscriptPopoverClose}
                        className="inline-flex w-20 items-center justify-center rounded-md bg-ktx-purple px-3 py-1 text-sm font-semibold text-white hover:bg-ktx-navy"
                        aria-label={`View ${liveTranscriptEntries.length} live transcript entries`}
                      >
                        ({liveTranscriptEntries.length})
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onMouseEnter={(event) => {
                          openCopiedPopover(row, {
                            triggerElement: event.currentTarget,
                            cursorX: event.clientX,
                            cursorY: event.clientY,
                          });
                        }}
                        onMouseLeave={scheduleCopiedPopoverClose}
                        onFocus={(event) => {
                          openCopiedPopover(row, {
                            triggerElement: event.currentTarget,
                          });
                        }}
                        onBlur={scheduleCopiedPopoverClose}
                        className="inline-flex w-20 items-center justify-center rounded-md bg-ktx-purple px-3 py-1 text-sm font-semibold text-white hover:bg-ktx-navy"
                        aria-label={`View ${copiedActionCount} copy events`}
                      >
                        ({copiedActionCount})
                      </button>
                    </td>
                  </tr>
                );
              })}
              {configuredRows.length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    className="px-4 py-6 text-center text-gray-500 font-medium"
                  >
                    No QA cases configured yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div
        className={`fixed z-50 overflow-hidden rounded-2xl bg-white shadow-2xl transition-all duration-200 ease-out flex flex-col ${
          hoveredCandidatePreview
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 translate-y-2 pointer-events-none"
        }`}
        style={{
          width: `min(${CANDIDATE_PREVIEW_WIDTH_PX}px, calc(100vw - 24px))`,
          minHeight: `min(${CANDIDATE_PREVIEW_MIN_HEIGHT_PX}px, calc(100vh - 24px))`,
          maxHeight: `min(${CANDIDATE_PREVIEW_MAX_HEIGHT_PX}px, calc(100vh - 24px))`,
          top: `${candidatePreviewPopoverPosition.top}px`,
          left: `${candidatePreviewPopoverPosition.left}px`,
        }}
        onMouseEnter={clearCandidatePreviewPopoverCloseTimeout}
        onMouseLeave={scheduleCandidatePreviewPopoverClose}
      >
        <div
          className="flex items-start justify-between border-b border-gray-200 px-5 py-4 bg-white"
          role="dialog"
          aria-modal="false"
          aria-label="Candidate job preview"
        >
          <div>
            <h4 className="text-[20px] font-semibold text-ktx-navy">
              Candidate Preview
            </h4>
            <p className="text-sm text-ktx-navy/80">
              Appointment {hoveredCandidatePreview?.appointmentId || "-"} | Patient{" "}
              {hoveredCandidatePreview?.patientId || "-"}
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  if (
                    hoveredCandidatePreview?.sourceCandidate &&
                    canSelectActiveCandidatePreviewEntry
                  ) {
                    chooseCandidate(hoveredCandidatePreview.sourceCandidate, {
                      transcribeJobName: String(
                        activeCandidatePreviewEntry?.transcribe_job_name || "",
                      ).trim(),
                      summarizeJobName: String(
                        activeCandidatePreviewEntry?.summarize_job_name || "",
                      ).trim(),
                      append:
                        selectedPatientId === hoveredCandidatePreview.patientId,
                    });
                  }
                }}
                disabled={
                  !canSelectActiveCandidatePreviewEntry ||
                  isActiveCandidatePreviewEntrySelected
                }
                className="rounded-md bg-ktx-purple px-3 py-1 text-sm font-semibold text-white hover:bg-ktx-navy disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isActiveCandidatePreviewEntrySelected
                  ? "Audio Added"
                  : "Add This Audio"}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (hoveredCandidatePreview?.sourceCandidate) {
                    void selectAllCandidateJobs(
                      hoveredCandidatePreview.sourceCandidate,
                    );
                  }
                }}
                disabled={
                  completeCandidatePreviewJobs.length === 0 ||
                  areAllCandidatePreviewJobsSelected ||
                  selectingPatientId === hoveredCandidatePreview?.patientId
                }
                className="rounded-md border border-ktx-purple bg-white px-3 py-1 text-sm font-semibold text-ktx-purple hover:bg-barely-purple disabled:cursor-not-allowed disabled:opacity-50"
              >
                {selectingPatientId === hoveredCandidatePreview?.patientId
                  ? "Adding..."
                  : areAllCandidatePreviewJobsSelected
                    ? "All Added"
                    : `Add All (${completeCandidatePreviewJobs.length})`}
              </button>
            </div>
            <div className="rounded-md border border-gray-200 px-3 py-1 text-xs text-ktx-navy/80 bg-gray-50">
              Hover to inspect
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-5 bg-white">
          {isCandidatePreviewLoading ? (
            <div className="h-full flex flex-col items-center justify-center gap-3">
              <div className="h-8 w-8 rounded-full border-4 border-ktx-purple/20 border-t-ktx-purple animate-spin" />
              <p className="text-sm font-medium text-ktx-navy/80">
                Loading preview...
              </p>
            </div>
          ) : activeCandidatePreviewEntry ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                <button
                  type="button"
                  className="rounded-md border border-gray-300 px-3 py-1 text-sm font-semibold text-ktx-navy disabled:opacity-40"
                  onClick={() =>
                    setCandidatePreviewEntryIndex(activeCandidatePreviewEntryIndex - 1)
                  }
                  disabled={activeCandidatePreviewEntryIndex <= 0}
                >
                  Prev
                </button>
                <div className="text-center">
                  <p className="text-sm font-semibold text-ktx-navy">
                    Audio {activeCandidatePreviewEntryIndex + 1} of{" "}
                    {hoveredCandidatePreviewEntries.length}
                  </p>
                  <p className="text-xs text-ktx-navy/70">
                    T:{" "}
                    {String(
                      activeCandidatePreviewEntry?.transcribe_job_name || "-",
                    )}{" "}
                    | S:{" "}
                    {String(
                      activeCandidatePreviewEntry?.summarize_job_name || "-",
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  className="rounded-md border border-gray-300 px-3 py-1 text-sm font-semibold text-ktx-navy disabled:opacity-40"
                  onClick={() =>
                    setCandidatePreviewEntryIndex(activeCandidatePreviewEntryIndex + 1)
                  }
                  disabled={
                    activeCandidatePreviewEntryIndex >=
                    hoveredCandidatePreviewEntries.length - 1
                  }
                >
                  Next
                </button>
              </div>
              <div
                className="grid grid-cols-1 gap-3 lg:grid-cols-2"
              >
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <p className="text-sm font-semibold text-ktx-navy mb-2">
                    Transcript
                  </p>
                  <textarea
                    value={String(
                      activeCandidatePreviewEntry?.transcript_text || "",
                    )}
                    readOnly
                    className="w-full min-h-[300px] resize-none rounded-xl bg-white p-3 text-ktx-navy border-none focus:outline-none"
                  />
                </div>
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <p className="text-sm font-semibold text-ktx-navy mb-2">
                    AI Note
                  </p>
                  <textarea
                    value={String(activeCandidatePreviewEntry?.ai_note || "")}
                    readOnly
                    className="w-full min-h-[300px] resize-none rounded-xl bg-white p-3 text-ktx-navy border-none focus:outline-none"
                  />
                </div>
                {hasCandidatePreviewChartHistory && (
                  <div className="flex max-h-[480px] min-h-0 flex-col rounded-xl border border-gray-200 bg-gray-50 p-4 lg:col-span-2">
                    <p className="mb-2 text-sm font-semibold text-ktx-navy">
                      CHAPI Data
                    </p>
                    <div
                      className="mb-2 flex gap-2 overflow-x-auto pb-1"
                      aria-label="CHAPI preview sections"
                    >
                      {Object.keys(candidatePreviewChartHistory).map((field) => (
                        <button
                          key={field}
                          type="button"
                          onClick={() =>
                            scrollToCandidatePreviewChapiField(field)
                          }
                          className="shrink-0 rounded-full border border-ktx-purple bg-white px-3 py-1 text-xs font-semibold text-ktx-purple transition hover:bg-ktx-purple hover:text-white"
                        >
                          {formatChartHistoryFieldLabel(field)}
                        </button>
                      ))}
                    </div>
                    <div
                      ref={candidatePreviewChapiScrollRef}
                      className="min-h-0 flex-1 overflow-auto rounded-xl bg-white p-3"
                    >
                      <ChartHistoryTable
                        chartHistory={candidatePreviewChartHistory}
                      />
                    </div>
                  </div>
                )}
                {hoveredCandidatePreview?.coding && (
                  <div className="lg:col-span-2">
                    <CodingPanel coding={hoveredCandidatePreview.coding} />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-ktx-navy/70">
              Transcript or AI note not available for this candidate.
            </div>
          )}
        </div>
      </div>

      <div
        className={`fixed z-50 overflow-hidden rounded-2xl bg-white shadow-2xl transition-all duration-200 ease-out flex flex-col ${
          hoveredLiveTranscriptCase
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 translate-y-2 pointer-events-none"
        }`}
        style={{
          width: `min(${COPIED_POPOVER_WIDTH_PX}px, calc(100vw - 24px))`,
          minHeight: `min(${COPIED_POPOVER_MIN_HEIGHT_PX}px, calc(100vh - 24px))`,
          maxHeight: `min(${COPIED_POPOVER_MAX_HEIGHT_PX}px, calc(100vh - 24px))`,
          top: `${liveTranscriptPopoverPosition.top}px`,
          left: `${liveTranscriptPopoverPosition.left}px`,
        }}
        onMouseEnter={clearLiveTranscriptPopoverCloseTimeout}
        onMouseLeave={scheduleLiveTranscriptPopoverClose}
      >
        <div
          className="flex items-start justify-between border-b border-gray-200 px-5 py-4 bg-white"
          role="dialog"
          aria-modal="false"
          aria-label="Live transcript entries"
        >
          <div>
            <h4 className="text-[20px] font-semibold text-ktx-navy">
              Live Transcript Entries
            </h4>
            <p className="text-sm text-ktx-navy/80">
              Appointment {hoveredLiveTranscriptCase?.appointment_id || "-"} | Patient{" "}
              {hoveredLiveTranscriptCase?.patient_id || "-"}
            </p>
            <p className="text-sm text-ktx-navy/70 mt-1">
              {liveTranscriptPopoverEntries.length} active user
              {liveTranscriptPopoverEntries.length === 1 ? "" : "s"}
            </p>
          </div>
          <div className="rounded-md border border-gray-200 px-3 py-1 text-sm text-ktx-navy/80 bg-gray-50">
            Hover to inspect
          </div>
        </div>

        <div className="flex-1 overflow-auto p-5 bg-white">
          {liveTranscriptPopoverEntries.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-ktx-navy/70">
              No live transcript entries saved yet.
            </div>
          ) : (
            <div className="space-y-3">
              {liveTranscriptPopoverEntries.map((entry) => (
                <div
                  key={entry.username}
                  className="rounded-xl border border-gray-200 bg-gray-50 p-4"
                >
                  <p className="text-sm font-semibold text-ktx-navy mb-2">
                    {entry.username}
                  </p>
                  <textarea
                    value={entry.text}
                    readOnly
                    className="w-full min-h-[140px] resize-none rounded-xl bg-white p-3 text-ktx-navy border-none focus:outline-none"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div
        className={`fixed z-50 overflow-hidden rounded-2xl bg-white shadow-2xl transition-all duration-200 ease-out flex flex-col ${
          hoveredCopiedCase
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 translate-y-2 pointer-events-none"
        }`}
        style={{
          width: `min(${COPIED_POPOVER_WIDTH_PX}px, calc(100vw - 24px))`,
          minHeight: `min(${COPIED_POPOVER_MIN_HEIGHT_PX}px, calc(100vh - 24px))`,
          maxHeight: `min(${COPIED_POPOVER_MAX_HEIGHT_PX}px, calc(100vh - 24px))`,
          top: `${copiedPopoverPosition.top}px`,
          left: `${copiedPopoverPosition.left}px`,
        }}
        onMouseEnter={clearCopiedPopoverCloseTimeout}
        onMouseLeave={scheduleCopiedPopoverClose}
      >
        <div
          className="flex items-start justify-between border-b border-gray-200 px-5 py-4 bg-white"
          role="dialog"
          aria-modal="false"
          aria-label="Copied activity details"
        >
          <div>
            <h4 className="text-[20px] font-semibold text-ktx-navy">
              Copied Activity
            </h4>
            <p className="text-sm text-ktx-navy/80">
              Appointment {hoveredCopiedCase?.appointment_id || "-"} | Patient{" "}
              {hoveredCopiedCase?.patient_id || "-"}
            </p>
            <p className="text-sm text-ktx-navy/70 mt-1">
              {copiedModalUsers.length} active user
              {copiedModalUsers.length === 1 ? "" : "s"} |{" "}
              {copiedModalTotalActions} total copy action
              {copiedModalTotalActions === 1 ? "" : "s"}
            </p>
          </div>
          <div className="rounded-md border border-gray-200 px-3 py-1 text-sm text-ktx-navy/80 bg-gray-50">
            Hover to inspect
          </div>
        </div>

        <div className="flex-1 overflow-auto p-5 bg-white">
          {copiedModalUsers.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-ktx-navy/70">
              No users have copied transcript, note, or live transcript for this
              case yet.
            </div>
          ) : (
            <div className="overflow-auto border border-gray-100 rounded-xl">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b border-gray-200 text-left text-gray-600">
                    <th className="px-4 py-3 font-semibold">Username</th>
                    <th className="px-4 py-3 font-semibold">Copied Items</th>
                    <th className="px-4 py-3 font-semibold text-center">Note</th>
                    <th className="px-4 py-3 font-semibold text-center">
                      Transcript
                    </th>
                    <th className="px-4 py-3 font-semibold text-center">
                      Live Transcript
                    </th>
                    <th className="px-4 py-3 font-semibold text-center">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {copiedModalUsers.map((user) => (
                    <tr key={user.username} className="border-b border-gray-100">
                      <td className="px-4 py-3 text-ktx-navy font-medium">
                        {user.username}
                      </td>
                      <td className="px-4 py-3 text-ktx-navy">
                        {user.items.length > 0 ? user.items.join(", ") : "-"}
                      </td>
                      <td className="px-4 py-3 text-ktx-navy text-center">
                        {user.noteCount}
                      </td>
                      <td className="px-4 py-3 text-ktx-navy text-center">
                        {user.transcriptCount}
                      </td>
                      <td className="px-4 py-3 text-ktx-navy text-center">
                        {user.liveTranscriptCount}
                      </td>
                      <td className="px-4 py-3 text-ktx-navy text-center font-semibold">
                        {user.totalCount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
